import Permissions, { usePermissions } from '@app/src/auth/permissions'
import type { NavigationItemProps } from '@app/src/components/Navigation/VerticalMenu/NavigationItems/NavigationItems'
import { insertIf } from '@app/src/utils/helpersTs'
import navigationLinks from '@app/src/wf-constants/navigationLinks'
import paths from '@app/src/wf-constants/paths'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import type { MenuWrapperProps } from './MenuWrapper'
import MenuWrapper from './MenuWrapper'

// Supply chain relations and mapping responses are tabs rather than menu items in the new product mapping UI,
// so no menu item links them - list their paths to keep the menu highlighted while they are open.
const TAB_ONLY_PATHS = [paths.mappingNodeCollection, paths.receivedMappingResponseCollection]

const ProductsMenu: React.FC<NavigationItemProps> = ({ icon }) => {
  const { formatMessage } = useIntl()
  const { productMapping, newProductMappingUi } = useFlags()
  const { hasPermission } = usePermissions()
  const hasFullProductMappingAccess = hasPermission(Permissions.FULL_DASHBOARD_ACCESS)
  const hasRespondAccess = hasPermission(Permissions.PRODUCT_MAPPING_RESPOND_ACCESS)
  const isSourcingUser = hasPermission(Permissions.SOURCING_USER)

  if (!productMapping || !isSourcingUser) return null

  const newMenuItems: MenuWrapperProps['menuItems'] = [
    {
      title: formatMessage({ id: 'navbar.allProducts' }),
      link: navigationLinks.products,
      permission: [Permissions.SOURCING_USER],
    },
    ...insertIf(hasRespondAccess, {
      title: formatMessage({ id: 'schemas.mappingRequest.receivedMappingRequests' }),
      link: navigationLinks.mappingRequests,
      permission: [Permissions.PRODUCT_MAPPING_RESPOND_ACCESS],
    }),
    ...insertIf(hasFullProductMappingAccess, {
      title: formatMessage({ id: 'schemas.mappingRequest.sentMappingRequests' }),
      link: navigationLinks.sentMappingRequests,
      permission: [Permissions.FULL_DASHBOARD_ACCESS],
    }),
  ]

  const legacyMenuItems: MenuWrapperProps['menuItems'] = [
    {
      title: formatMessage({ id: 'navbar.allProducts' }),
      link: navigationLinks.products,
      permission: [Permissions.SOURCING_USER],
    },
    {
      title: formatMessage({ id: 'navbar.valueChains' }),
      link: navigationLinks.valueChains,
      permission: [Permissions.SOURCING_USER],
    },
    ...insertIf(hasFullProductMappingAccess, {
      title: formatMessage({ id: 'navbar.sentMappingRequests' }),
      link: navigationLinks.sentMappingRequests,
      permission: [Permissions.FULL_DASHBOARD_ACCESS],
    }),
    ...insertIf(hasFullProductMappingAccess, {
      title: formatMessage({ id: 'navbar.allMappingResponses' }),
      link: navigationLinks.receivedMappingResponses,
      permission: [Permissions.FULL_DASHBOARD_ACCESS],
    }),
  ]

  return (
    <MenuWrapper
      icon={icon}
      menuItems={newProductMappingUi ? newMenuItems : legacyMenuItems}
      activePaths={TAB_ONLY_PATHS}
      title={formatMessage({ id: 'navbar.productMapping' })}
    />
  )
}

export default ProductsMenu
